import AddIcon from '@mui/icons-material/Add'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Box,
  Button,
  Fade,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Typography,
} from '@mui/material'
import { SnackBarContext } from 'components'
import { Icon } from 'components/icon'
import { ConfirmPopup } from 'components/inlineConfirmPopup'
import { theme } from 'config/theme'
import { inject, observer } from 'mobx-react'
import React from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Container, Draggable } from 'react-smooth-dnd'
import {  Pages, RoutingParams } from 'routes'
import styled from 'styled-components'
import 'suneditor/dist/css/suneditor.min.css'
import { StepType, DataStatus } from 'types'
import { EditorInput } from 'views'
import { Course, Step } from '../../models'
import { RoutingStore } from '../../stores'

const MenuItemIcon = styled(Icon)`
  margin-right: ${theme.spacing(1)};
`

const stepTypes: StepType[] = [StepType.Read, StepType.Quiz, StepType.Video, StepType.Grid]
export interface ISidebarCourseEditorProps {
  course:        Course,
  routingStore?: RoutingStore,
}

export const SidebarCourseEditor = inject('routingStore')(observer((props: ISidebarCourseEditorProps) => {
  const { routingStore, course } = props;
  const { t } = useTranslation();
  const { toast } = React.useContext(SnackBarContext);
  const params: RoutingParams = useParams<RoutingParams>();

  const [openConfirmPopup, setOpenConfirmPopup] = React.useState(false);
  const [anchorEl,         setAnchorEl]         = React.useState<null | HTMLElement>(null);
  const [selectedChapter,  setSelectedChapter]  = React.useState<Step>(null);
  const [selectedStep,     setSelectedStep]     = React.useState<Step>(null);
  const [mandatory,        setMandatory]        = React.useState<boolean>(false);
  const [open,             setOpen]             = React.useState<Step>(null);

  const chapterList = React.useRef<Draggable>();

  const [stepEl,    setStepEl]    = React.useState<null | HTMLElement>(null);
  const [chapterEl, setChapterEl] = React.useState<null | HTMLElement>(null);
  React.useEffect(() => {
    if (selectedStep || selectedChapter)
      setOpen(selectedStep || selectedChapter);
  }, [selectedChapter, selectedStep]);

  React.useEffect(() => {
    if (selectedStep) setMandatory(selectedStep.mandatory);
  }, [selectedStep]);

  const chapters = course?.getChapters || [];

  return routingStore.sidebarContainer
    ? ReactDOM.createPortal(
        <>
          <Box
            display       = 'flex'
            flexDirection = 'column'
            alignItems    = 'left'
            mt            = '2rem'
          >
            <ListItem
              sx={{
                padding: '0px 16px'
              }}
            >
              <ListItemButton
                onClick = {() => routingStore.push(Pages.Courses)}
                sx      = {{ borderRadius: '6px' }}
              >
                <ListItemIcon>
                  <Icon iconId='arrow-left' size='size-24' />
                </ListItemIcon>
                <ListItemText primary={t('back_to_menu')} />
              </ListItemButton>
            </ListItem>
            <ListItem
              selected = {params.view === 'settings'}
              sx       = {{
                padding: '0px 16px'
              }}
            >
              <ListItemButton
                sx      = {{ borderRadius: '6px' }}
                onClick = {() => {
                  routingStore.push(Pages.Editor, {
                    id:   '' + course.id,
                    view: 'settings',
                  });
                  course.setCurStep(null);
                }}
              >
                <ListItemIcon>
                  <Icon iconId='settings' size='size-24' />
                </ListItemIcon>
                <ListItemText primary={t('course_settings')} />
              </ListItemButton>
            </ListItem>
            <ListItem 
              selected = {params.view === 'notes'}
              sx       = {{
                padding: '0px 16px'
              }}
            >
              <ListItemButton
                sx      = {{ borderRadius: '6px' }}
                onClick = {() => {
                  routingStore.push(Pages.Editor, {
                      id:   '' + course.id,
                      view: 'notes',
                  });
                  course.setCurStep(null);
                }}
              >
                <ListItemIcon>
                  <Icon iconId='notes-edit' size='size-24' />
                </ListItemIcon>
                <ListItemText primary={t('notes')} />
              </ListItemButton>
            </ListItem>
          </Box>
          <List
            component = 'nav'
            sx        = {{
              color:         theme.palette.primary.contrastText,
              width:         '100%',
              flexDirection: 'column',
            }}
          >
            <Container
              dragHandleSelector = '.drag_handle'
              lockAxis           = 'y'
              onDrop             = {(props: {
                removedIndex: number,
                addedIndex:   number
              }) => {
                course.rearrangeStep(
                  null,
                  props.removedIndex,
                  props.addedIndex
                )
              }}
            >
              {/* CHAPTER LIST */}
              {chapters.map((chapter, chapter_idx) => (
                <Draggable key={chapter.id} ref={chapterList}>
                  <>
                    <StepListItem
                      className       = 'drag_handle'
                      secondaryAction = {
                        <IconButton
                          sx      = {{ color: 'white', right: '1.5rem' }}
                          edge    = 'end'
                          onClick = {(
                            event: React.MouseEvent<HTMLButtonElement>
                          ) => {
                            setAnchorEl(event.currentTarget);
                            setSelectedStep(null);
                            setSelectedChapter(chapter);
                          }}
                        >
                          <MoreVertIcon
                            htmlColor={theme.palette.white.main}
                          />
                        </IconButton>
                      }
                    >
                      <ChapterItem
                        selected={
                          course.curStep?.id === chapter.id &&
                          params.view !== 'settings' &&
                          params.view !== 'notes'
                        }
                        onClick={() => {
                          course.setCurStep(chapter);
                          setAnchorEl(null);
                          if (params.view)
                            routingStore.push(Pages.Editor, {
                                id: '' + course.id,
                            });
                        }}
                      >
                        {(true || course.status === DataStatus.Draft) && 
                          <DragIcon
                            iconId    = 'drag-handle'
                            className = 'drag-handle'
                          />
                        }
                        <EditorInput
                          editOnFocus
                          autoFocus
                          blurOnEnter
                          fontWeight   = {theme.typography.fontWeightBold}
                          defaultValue = {chapter.name}
                          onFocus      = {() => setSelectedChapter(chapter)}
                          // prefix       = {`${chapter_idx + 1}`}
                          onBlur       = {async (value: string) => {
                            setSelectedChapter(null);
                            if (!(await chapter.set('name', value, true))) {
                              console.error('Change failed');
                              toast(t('toast_error_change_name'), 'error');
                            }
                          }}
                        />
                      </ChapterItem>
                    </StepListItem>
                  </>

                  <List component='div' disablePadding>
                    {/* STEP LIST */}
                    <Container
                      dragHandleSelector = '.drag_handle'
                      lockAxis           = 'y'
                      style              = {{ minHeight: 'fit-content' }}
                      onDrop             = {(props: {
                        removedIndex: number,
                        addedIndex:   number
                      }) => {
                        course.rearrangeStep(
                          chapter.id,
                          props.removedIndex,
                          props.addedIndex
                        )
                      }}
                    >
                      {course
                        .getStepsByChapter(chapter.id)
                        .map((step: Step, step_idx) => (
                          <Draggable key={step.id}>
                            <StepListItem
                              key             = {step.id}
                              secondaryAction = {
                                <IconButton
                                  sx      = {{ right: '1.5rem' }}
                                  onClick = {(
                                    event: React.MouseEvent<HTMLButtonElement>
                                  ) => {
                                    setAnchorEl(event.currentTarget);
                                    setSelectedChapter(null);
                                    setSelectedStep(step);
                                  }}
                                >
                                  <MoreVertIcon
                                    htmlColor={theme.palette.white.main}
                                  />
                                </IconButton>
                              }
                            >
                              <StepItem
                                sx       = {{fontSize: '16px'}}
                                selected = {
                                  course.curStep?.id === step.id &&
                                  params.view !== 'settings' &&
                                  params.view !== 'notes'
                                }
                                onClick = {() => {
                                  course.setCurStep(step);
                                  setSelectedStep(step);
                                  setAnchorEl(null);
                                  if (params.view)
                                    routingStore.push(Pages.Editor, {id: '' + course.id});
                                }}
                              >
                                {(true || course.status === DataStatus.Draft) && 
                                  <DragIcon
                                    iconId    = 'drag-handle'
                                    className = 'drag_handle'
                                  />
                                }
                                <EditorInput
                                  editOnFocus
                                  autoFocus
                                  blurOnEnter
                                  fontSize     = {theme.typography.fontSize}
                                  fontWeight   = {theme.typography.fontWeightRegular}
                                  defaultValue = {step.name}
                                  onFocus      = {() => setSelectedStep(step)}
                                  // prefix       = {`${chapter_idx + 1}.${step_idx + 1}`}
                                  onBlur       = {async (value: string) => {
                                    setSelectedStep(null);
                                    if (!(await step.set('name', value, true))) {
                                      console.error('Change failed');
                                      toast(t('toast_error_change_name'), 'error');
                                    }
                                  }}
                                />
                              </StepItem>
                            </StepListItem>
                          </Draggable>
                        ))}
                    </Container>
                    {course?.canEdit && 
                      <ListItem  sx={{ pl: '2.8rem' }}>
                        <AddIcon
                          sx        = {{ fontSize: '1.4rem' }}
                          cursor    = 'pointer'
                          htmlColor = {theme.palette.white.main}
                        />
                        <Typography
                          sx            = {{ cursor: 'pointer', paddingLeft: 1 }}
                          fontWeight    = {theme.typography.fontWeightBold}
                          variant       = 'subtitle2'
                          textTransform = 'uppercase'
                          color         = 'white'
                          onClick       = {(e) => {
                            setSelectedChapter(chapter);
                            setStepEl(e.currentTarget);
                          }}
                        >
                          {t('add_step')}
                        </Typography>
                        <Menu
                          id            = 'step-content-menu'
                          anchorEl      = {stepEl}
                          open          = {Boolean(stepEl)}
                          onClose       = {() => setStepEl(null)}
                          MenuListProps = {{'aria-labelledby': 'basic-button'}}
                        >
                          {stepTypes.map((type) => (
                            <MenuItem
                              key     = {type}
                              onClick = {async () => {
                                const step: Step = await course.addStep(selectedChapter.id, type)
                                if (step) course.setCurStep(step);
                                else toast(t('toast_error_add_step'), 'error');
                                setStepEl(null);
                              }}
                            >
                              <MenuItemIcon iconId={type} size='size-24' />
                              {t(type)}
                            </MenuItem>
                          ))}
                        </Menu>
                      </ListItem>
                    }
                  </List>
                </Draggable>
              ))}
            </Container>
            {course?.canEdit && 
              <Box
                alignItems     = 'center'
                mt             = '1rem'
                ml             = '2.4rem'
                display        = 'flex'
                justifyContent = 'flex-start'
              >
                <Button
                  onClick   = {(event) => setChapterEl(event.currentTarget)}
                  variant   = 'contained'
                  startIcon = {<Icon iconId='add' />}
                >
                  {t('add_chapter')}
                </Button>
                <Menu
                  id            = 'chapter-content-menu'
                  anchorEl      = {chapterEl}
                  open          = {Boolean(chapterEl)}
                  onClose       = {() => setChapterEl(null)}
                  MenuListProps = {{'aria-labelledby': 'basic-button'}}
                >
                  {stepTypes.map((type) => (
                    <MenuItem
                      key     = {type}
                      onClick = {async () => {
                        const chapter: Step = await course.addChapter(type);
                        if (chapter) course.setCurStep(chapter);
                        else toast(t('toast_error_add_step'), 'error');
                        setChapterEl(null);
                      }}
                    >
                      <MenuItemIcon iconId={type} size='size-24' />
                      {t(type)}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            }
          </List>
          {/* STEP MENU */}
          <Menu
            TransitionComponent = {Fade}
            anchorEl            = {anchorEl}
            open                = {Boolean(anchorEl && (selectedStep || selectedChapter))}
            sx                  = {{ top: '-5rem', left: '5rem' }}
            onClose             = {() => {
              setAnchorEl(null);
              setSelectedStep(null);
              setSelectedChapter(null);
              setOpenConfirmPopup(false);
            }}
          >
            <ConfirmPopup
              placement = 'right-start'
              open      = {openConfirmPopup}
              onCancel  = {() => {
                setOpenConfirmPopup(false);
                setAnchorEl(null);
              }}
              onConfirm={() => {
                course.removeStep(selectedStep?.id || selectedChapter.id)
                setAnchorEl(null);
                setSelectedStep(null);
                setSelectedChapter(null);
                setOpenConfirmPopup(false);
              }}
            >
              <MenuItem
                onClick  = {() => setOpenConfirmPopup(true)}
                disabled = {!course?.canEdit}
              >
                {`Delete ${!open?.isChapter ? 'step' : 'chapter'}`}
              </MenuItem>
            </ConfirmPopup>
            <MenuItem>
              <StyledFormControlLabel
                labelPlacement = 'start'
                label          = 'Mandatory'
                control        = {
                  <Switch
                    checked  = {mandatory}
                    name     = 'mandatory'
                    disabled = {!course?.canEdit}
                    onChange = {() => {
                      setMandatory(!mandatory);
                      selectedStep.set(
                        'mandatory',
                        !selectedStep.mandatory,
                        true
                      );
                    }}
                  />
                }
              />
            </MenuItem>
          </Menu>
        </>,
        routingStore.sidebarContainer
      )
    : null;
}));

const StepListItem = styled(ListItem)`
  padding: 0px 16px;
  svg {
    opacity: 0;
  }

  :hover {
    svg {
      opacity: 1;
    }
  }
`;

const ChapterItem = styled(ListItemButton)`
  position:        relative;
  display:         flex;
  justify-content: space-between;
  align-items:     center;
  padding-left:    3rem;
  border-radius:   6px;

  & + .MuiListItemSecondaryAction-root {
    right: 0;

    .MuiIconButton-root {
      margin-right: 0;
    }
  }
`;

const StepItem = styled(ChapterItem)`
  padding-left:  4rem;
  font-size:     16px;
  border-radius: 6px;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  &.MuiFormControlLabel-root {
    margin-left: 0;
  }
`;

const DragIcon = styled(Icon)`
  position:  absolute;
  left:      0rem;
  top:       50%;
  cursor:    grab;
  transform: translateY(-50%);

  &:active {
    cursor: grabbing;
  }
`;
